import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

// Customer dashboard pages that can be toggled per user
// Dashboard and Berichtenbox are always available (standard)
export const customerPageModules = [
  { key: "orders", label: "Bestellingen" },
  { key: "shipments", label: "Zendingen" },
  { key: "invoices", label: "Facturen" },
  { key: "production", label: "Productie" },
] as const;

export type CustomerPageKey = typeof customerPageModules[number]["key"];

/** Fetch page permissions for a specific user (admin use) */
export function useUserPagePermissionsForUser(userId: string | undefined) {
  return useQuery({
    queryKey: ["user-page-permissions", userId],
    queryFn: async () => {
      if (!userId) return {};
      const { data, error } = await supabase
        .from("user_page_permissions")
        .select("page_key, enabled")
        .eq("user_id", userId);
      if (error) throw error;
      const map: Record<string, boolean> = {};
      data?.forEach((r: any) => { map[r.page_key] = r.enabled; });
      return map;
    },
    enabled: !!userId,
  });
}

/** Fetch page permissions for ALL users at once (admin list view) */
export function useAllUserPagePermissions() {
  return useQuery({
    queryKey: ["all-user-page-permissions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_page_permissions")
        .select("user_id, page_key, enabled");
      if (error) throw error;
      const map: Record<string, Record<string, boolean>> = {};
      data?.forEach((r: any) => {
        if (!map[r.user_id]) map[r.user_id] = {};
        map[r.user_id][r.page_key] = r.enabled;
      });
      return map;
    },
  });
}

/** Fetch current user's own page permissions (realtime) */
export function useMyPagePermissions() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel(`my-page-perms-${user.id}-${Math.random().toString(36).slice(2)}`)
      .on("postgres_changes", {
        event: "*",
        schema: "public",
        table: "user_page_permissions",
        filter: `user_id=eq.${user.id}`,
      }, () => {
        queryClient.invalidateQueries({ queryKey: ["my-page-permissions", user.id] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id, queryClient]);

  return useQuery({
    queryKey: ["my-page-permissions", user?.id],
    queryFn: async () => {
      if (!user) return {};
      const { data, error } = await supabase
        .from("user_page_permissions")
        .select("page_key, enabled")
        .eq("user_id", user.id);
      if (error) throw error;
      const map: Record<string, boolean> = {};
      data?.forEach((r: any) => { map[r.page_key] = r.enabled; });
      return map;
    },
    enabled: !!user,
  });
}

/** Toggle a page permission for a user (upsert) */
export function useTogglePagePermission() {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();

  return useMutation({
    mutationFn: async ({ userId, pageKey, enabled }: { userId: string; pageKey: string; enabled: boolean }) => {
      const { error } = await supabase
        .from("user_page_permissions")
        .upsert(
          { user_id: userId, page_key: pageKey, enabled, updated_at: new Date().toISOString(), updated_by: currentUser?.id },
          { onConflict: "user_id,page_key" }
        );
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["user-page-permissions", vars.userId] });
      queryClient.invalidateQueries({ queryKey: ["all-user-page-permissions"] });
      queryClient.invalidateQueries({ queryKey: ["my-page-permissions"] });
    },
  });
}
